import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PwaService } from '../../../core/services/pwa.service';
import { GlassCardComponent } from '../glass-card/glass-card.component';

@Component({
  selector: 'app-pwa-install-banner',
  standalone: true,
  imports: [CommonModule, GlassCardComponent],
  template: `
    @if (canInstall() && !dismissed()) {
      <app-glass-card tint="primary" padding="md" class="install-banner">
        <div class="install-banner__content">
          <div class="install-banner__icon">
            <img src="/assets/icons/plumpy-download.png" alt="نصب اپ" class="icon-plumpy" />
          </div>
          <div class="install-banner__text">
            <h4>تقویم پریود را نصب کنید</h4>
            <p>برای دسترسی سریع‌تر و تجربه بهتر، این اپ را روی دستگاه خود نصب کنید.</p>
          </div>
        </div>
        <div class="install-banner__actions">
          <button class="install-banner__dismiss glass-interactive" (click)="dismiss()">
            بعداً
          </button>
          <button class="install-banner__install glass-interactive" (click)="install()">
            نصب
          </button>
        </div>
      </app-glass-card>
    }
  `,
  styles: [`
    .install-banner {
      animation: slide-up 0.4s var(--ease-out-spring) both;
      margin-bottom: var(--space-md);
    }

    @keyframes slide-up {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .install-banner__content {
      display: flex;
      align-items: flex-start;
      gap: var(--space-md);
    }

    .install-banner__icon {
      width: 48px;
      height: 48px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--glass-bg-tinted-primary);
      border-radius: var(--radius-lg);
      box-shadow: var(--glass-shadow-glow-primary);
    }

    .install-banner__icon img {
      width: 24px;
      height: 24px;
    }

    .install-banner__text {
      flex: 1;
      min-width: 0;
    }

    .install-banner__text h4 {
      margin: 0 0 var(--space-xs);
      @include mixins.text-h3;
      color: var(--text-on-glass-strong);
    }

    .install-banner__text p {
      margin: 0;
      font-size: 0.82rem;
      color: var(--text-on-glass-medium);
      @include mixins.text-caption;
    }

    .install-banner__actions {
      display: flex;
      justify-content: flex-end;
      gap: var(--space-sm);
      margin-top: var(--space-md);
      padding-top: var(--space-md);
      border-top: 1px solid var(--glass-border-soft);
    }

    .install-banner__dismiss {
      padding: var(--space-xs) var(--space-md);
      border-radius: var(--radius-full);
      font-size: 0.82rem;
      color: var(--text-on-glass-medium);
      background: var(--glass-bg-light);
      @include mixins.glass-interactive;
      min-height: 36px;

      &:focus-visible {
        @include mixins.focus-ring-spring();
      }
    }

    .install-banner__install {
      padding: var(--space-xs) var(--space-lg);
      border-radius: var(--radius-full);
      font-size: 0.82rem;
      font-weight: 700;
      color: var(--text-on-glass-strong);
      background: var(--glass-bg-tinted-secondary);
      @include mixins.glass-interactive;
      min-height: 36px;
      box-shadow: var(--glass-shadow-glow-secondary);

      &:focus-visible {
        @include mixins.focus-ring-spring();
      }
    }
  `],
})
export class PwaInstallBannerComponent implements OnInit, OnDestroy {
  private readonly pwaService = inject(PwaService);

  canInstall = signal(false);
  dismissed = signal(false);
  private dismissedKey = 'pwa-install-dismissed';

  ngOnInit(): void {
    const stored = localStorage.getItem(this.dismissedKey);
    if (stored === 'true') {
      this.dismissed.set(true);
      return;
    }

    this.pwaService.canInstall$.subscribe((can) => this.canInstall.set(can));
  }

  ngOnDestroy(): void {
    if (this.dismissed()) {
      localStorage.setItem(this.dismissedKey, 'true');
    }
  }

  install(): void {
    this.pwaService.promptInstall().then((accepted) => {
      if (accepted) {
        this.dismissed.set(true);
      }
    });
  }

  dismiss(): void {
    this.dismissed.set(true);
    this.pwaService.dismissInstallPrompt();
  }
}